class ShoppableStickyCart extends HTMLElement {
  constructor() {
    super();
    this.productFormSelector = this.getAttribute('product-form-selector');
    this.variantSelectors = JSON.parse(this.getAttribute('variant-selectors'));
    this.addToCartButtonSelector = this.getAttribute('add-to-cart-button');
    this.addToCartAvailable = this.getAttribute('add-to-cart-available');
    this.addToCartUnavailable = this.getAttribute('add-to-cart-unavailable');

    this.productForm = null;
    this.addToCartButton = null;
    this.footer = null;
    this.selectElements = [];
    this.currentVariant = null;
    this.productData = null;
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.buttonPastScreen = false;
    this.footerInView = false;
  }

  connectedCallback() {
    this.productData = this._getMasterVariantData();
    this.productForm = document.querySelector(this.productFormSelector);
    this.addToCartButton = document.querySelector(this.addToCartButtonSelector);

    if (this.variantSelectors) {
    this.selectElements = this.variantSelectors
      .map(selector => document.querySelector(selector))
      .filter(sel => sel && !sel.closest('.complementary-products'));
    }

    this.footer = document.querySelector('footer');

    this.insertVariantSelectors();
    this.setupIntersectionObserver();
    this.setupVariantSync();
    this.setupEventListeners();

    this.classList.remove('visible');
  }

  insertVariantSelectors() {
    const variantSelectorsContainer = this.querySelector('.variant-selectors');
    if (!variantSelectorsContainer) return;

    const oldHTML = variantSelectorsContainer.innerHTML;
    if (this.selectElements && this.selectElements[2] != null) return '';

    const variantOptionsHtml = this.selectElements.map((sel, index) => {
      if (!sel) return '';
      let optionsHtml = '';
      for (let i = 0; i < sel.options.length; i++) {
        const opt = sel.options[i];
        optionsHtml += `<option value="${opt.value}">${opt.text}</option>`;
      }
      return `<div class="select"><select class="select__select" data-variant-index="${index}">${optionsHtml}</select>${oldHTML}</div>`;
    }).join('');

    variantSelectorsContainer.innerHTML = variantOptionsHtml;
  }

  setupEventListeners() {
    if (this.selectElements[2] == null) {
      const addToCartBtn = this.querySelector('#sticky-add-to-cart');
      if (addToCartBtn) {
        addToCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.addToCart();
        });
      }
    } else {
      const addToCartBtn = this.querySelector('#sticky-add-to-cart');
      const addToCartBtnNoVariants = this.querySelector('#sticky-add-to-cart-no-variants');
      const variantSelectors = this.querySelector('#sticky-cart-variant-selectors');
      addToCartBtn.classList.add('hidden');
      variantSelectors.classList.add('hidden');
      addToCartBtnNoVariants.classList.remove('hidden');
    }

    this.querySelectorAll('[data-variant-index]').forEach(sel => {
      sel.addEventListener('change', () => {
        this.updateMainSelectorsFromWidget();
      });
    });
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.target === this.addToCartButton) {
          if (entry.isIntersecting) {
            this.buttonPastScreen = false;
          } else {
            const rect = entry.boundingClientRect;
            if (rect.bottom < 0) {
              this.buttonPastScreen = true;
            } else {
              this.buttonPastScreen = false;
            }
          }
        } else if (entry.target === this.footer) {
          this.footerInView = entry.isIntersecting;
        }
      });

      this.updateVisibility();
    }, { threshold: 0 });

    if (this.addToCartButton) observer.observe(this.addToCartButton);
    if (this.footer) observer.observe(this.footer);
  }

  updateVisibility() {
    const body = document.body;
    if (this.buttonPastScreen && !this.footerInView) {
      this.classList.add('visible');
      body.classList.add('has-sticky-cart');
    } else {
      this.classList.remove('visible');
      body.classList.remove('has-sticky-cart');
    }
  }

  setupVariantSync() {
    this.selectElements.forEach((sel, idx) => {
      if (!sel) return;
      sel.addEventListener('change', () => {
        this.updateWidgetFromMainSelectors();
      });
    });
    this.updateWidgetFromMainSelectors();
  }

  updateWidgetFromMainSelectors() {
    this.selectElements.forEach((mainSel, idx) => {
      const widgetSel = this.querySelector(`[data-variant-index="${idx}"]`);
      if (widgetSel && mainSel && widgetSel.value !== mainSel.value) {
        widgetSel.value = mainSel.value;
      }
    });
    this.updateCurrentVariant();
  }

  updateMainSelectorsFromWidget() {
    this.selectElements.forEach((mainSel, idx) => {
      const widgetSel = this.querySelector(`[data-variant-index="${idx}"]`);
      if (mainSel && widgetSel && mainSel.value !== widgetSel.value) {
        mainSel.value = widgetSel.value;
        mainSel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    this.updateCurrentVariant();
  }

  updateCurrentVariant() {
    if (!this.productData || !this.productData.variants) {
      console.warn('Sin datos de producto o variantes disponibles.');
      return;
    }

    if (this.productData.variants.length === 1) {
      this.currentVariant = this.productData.variants[0];
    } else {
      const selectedOptions = this.selectElements.map(s => s ? s.value : null);
      const variant = this.productData.variants.find(v =>
        v.options.every((opt, index) => opt === selectedOptions[index])
      );

      this.currentVariant = variant;
    }

    const addToCartBtn = this.querySelector('#sticky-add-to-cart');
    if (addToCartBtn) {
      if (!this.currentVariant || !this.currentVariant.available) {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = this.addToCartUnavailable;
      } else {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = this.addToCartAvailable;
      }
    }

    const priceElement = this.querySelector('#sticky-cart-price');
    if (!priceElement) return;

    if (!this.currentVariant) {
      priceElement.innerHTML = '';
      priceElement.classList.remove('price--on-sale');
      return;
    }

    const finalPrice = this.currentVariant.price;
    const comparePrice = this.currentVariant.compare_at_price;

    const formatMoney = (cents) => {
      return new Intl.NumberFormat(Shopify.locale, {
        style: 'currency',
        currency: Shopify.currency.active
      }).format(cents / 100);
    };

    const onSale = comparePrice > finalPrice;
    priceElement.classList.toggle('price--on-sale', onSale);

    if (this.dataset.newPrice === 'true') {
      let discountBadge = '';
      if (onSale) {
        const percentage = Math.round((comparePrice - finalPrice) * 100 / comparePrice);
        const badge = document.createElement('span');
        badge.className = 'sticky-cart__factory-discount';
        badge.textContent = `-${percentage}%`;
        badge.setAttribute('aria-label', this.dataset.discountLabel.replace('[percentage]', percentage));
        discountBadge = badge.outerHTML;
      }

      priceElement.innerHTML = `
        <div class="sticky-cart__factory-prices">
          ${discountBadge}
          <span class="sticky-cart__factory-amount">${formatMoney(finalPrice)}</span>
          ${onSale ? `<s class="sticky-cart__factory-compare">${formatMoney(comparePrice)}</s>` : ''}
        </div>
      `;
      return;
    }

    if (comparePrice && comparePrice > finalPrice) {
      priceElement.innerHTML = `
        <div class="price__container">
          <div class="price__sale">
            <span class="price-item price-item--regular">
              ${formatMoney(comparePrice)}
            </span>
            <span class="price-item price-item--sale price-item--last">
              ${formatMoney(finalPrice)}
            </span>
          </div>
        </div>
      `;
      priceElement.classList.add('price--on-sale');
    } else {
      priceElement.innerHTML = `
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular">
              ${formatMoney(finalPrice)}
            </span>
          </div>
        </div>
      `;
    }
  }

  addToCart() {
    if (!this.currentVariant) return;

    if (this.cart) {
      this.cart.setActiveElement(document.activeElement);
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.productForm);
    formData.set('id', this.currentVariant.id);
    formData.set('properties[_sticky]', true);
    if (this.cart) {
      formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      this.cart.setActiveElement(document.activeElement);
    }
    config.body = formData;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData)
    })
    .then(response => response.json())
    .then(response => {
      if (response.status) {
        this.handleErrorMessage(response.description);
        this.error = true;
        return;
      } else if (!this.cart) {
        window.location = window.routes.cart_url;
        return;
      }

      if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form'});
      this.cart.renderContents(response);
    })
    .catch(err => console.error('Error al agregar al carrito:', err))
    .finally(() => {
      if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
    });
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  _getMasterVariantData() {
    let masterVariants = document.querySelector('variant-selects');
    if (!masterVariants) {
      masterVariants = document.querySelector('.product-sticky-cart__content_options');
      if (!masterVariants) {
        console.warn('No se encontró <variant-selects> ni .product-sticky-cart__content_options');
        return null;
      }
    }

    const productJsonElement = masterVariants.querySelector(':not(.complementary-products) [type="application/json"]');
    if (!productJsonElement) {
      console.warn('No se encontró un script con type="application/json" en masterVariants.');
      return null;
    }

    try {
      const variants = JSON.parse(productJsonElement.textContent);
      return { variants: variants };
    } catch (e) {
      console.error('Error al parsear el JSON de variantes:', e);
      return null;
    }
  }
}

customElements.define('shoppable-sticky-cart', ShoppableStickyCart);
